import type { ReactNode } from 'react';
import { Icon } from '../brand';
import './how-it-works.css';

// "How it works" storyboard on the public homepage: five pictures, one short
// caption each, for how SuperIndent works today.
// Example numbers only; nothing reads or writes real records.

function Plate() {
  return <span className="hiw-plate"><i>IND</i>TN 09 AB 1234</span>;
}

function QrPattern() {
  // A fixed decorative pattern, not a scannable code.
  const cells: ReactNode[] = [];
  for (let r = 0; r < 17; r++) for (let c = 0; c < 17; c++) {
    if ((r < 5 && c < 5) || (r < 5 && c > 11) || (r > 11 && c < 5)) continue;
    if (((r * 7 + c * 11 + r * c) % 5) < 2) cells.push(<rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" />);
  }
  const finder = (x: number, y: number) => <path key={`f${x}${y}`} d={`M${x} ${y}h5v5h-5zM${x + 1} ${y + 1}v3h3v-3zM${x + 2} ${y + 2}h1v1h-1z`} fillRule="evenodd" />;
  return <svg viewBox="0 0 17 17" className="hiw-qr" aria-hidden="true">{finder(0, 0)}{finder(12, 0)}{finder(0, 12)}{cells}</svg>;
}

function Phone({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`hiw-pic hiw-phone ${className}`}><div className="hiw-phone-notch" />{children}</div>;
}

const SCENES: { title: string; caption: string; picture: ReactNode }[] = [
  {
    title: 'The fleet owner raises an indent',
    caption: 'The fleet owner or fleet manager enters the driver or vehicle, fuel and limit. If it is within the pump’s credit terms, its QR code is ready.',
    picture: (
      <div className="hiw-pic hiw-approve">
        <div className="hiw-approve-head"><Icon name="truck" size={20} />Example Logistics</div>
        <Plate />
        <div className="hiw-approve-row"><span><Icon name="fuel" size={16} />Diesel</span><b>₹3,000</b></div>
        <div className="hiw-stamp"><Icon name="check" size={16} stroke={3} />Approved</div>
      </div>
    ),
  },
  {
    title: 'The QR goes to the driver on WhatsApp',
    caption: 'The fleet manager sends the QR and 8-digit code on WhatsApp. The driver shows it at the pump. No app needed.',
    picture: <Phone><QrPattern /><span className="hiw-code">4821 7730</span></Phone>,
  },
  {
    title: 'The attendant checks the indent',
    caption: 'The attendant scans or enters the code, checks the details, then starts filling within the approved limit.',
    picture: (
      <Phone className="hiw-phone-ok">
        <span className="hiw-ok-tick"><Icon name="check" size={26} stroke={3.2} /></span>
        <b>OK, fill fuel</b>
        <small>Stop at ₹3,000</small>
      </Phone>
    ),
  },
  {
    title: 'The attendant saves the fill',
    caption: 'After filling, the attendant saves the actual litres and amount. Numbers read from a receipt photo are checked first.',
    picture: (
      <Phone className="hiw-phone-entry">
        <div className="hiw-entry"><small>Litres</small><b>25.00 L</b></div>
        <div className="hiw-entry"><small>Amount</small><b>₹2,368.00</b></div>
        <span className="hiw-save">Save</span>
      </Phone>
    ),
  },
  {
    title: 'The driver confirms the fill',
    caption: 'The driver gets an SMS with the litres, amount and a code, and tells the code to the attendant. The fleet accounts team can confirm instead.',
    picture: (
      <div className="hiw-pic hiw-books">
        <div className="hiw-book-row"><span><Icon name="truck" size={16} />Fleet owner</span><span><Icon name="store" size={16} />Pump</span></div>
        <div className="hiw-book">
          <small>Fuel entry</small>
          <div><span>TN 09 AB 1234 · Diesel</span><b>₹2,368.00</b></div>
        </div>
        <div className="hiw-bill"><Icon name="check" size={16} stroke={3} />Confirmed</div>
      </div>
    ),
  },
];

export function HowItWorks() {
  return (
    <section id="how-it-works" className="hiw landing-width" aria-labelledby="hiw-title">
      <div className="hiw-head">
        <p className="landing-eyebrow">HOW IT WORKS</p>
        <h2 id="hiw-title">How your fleet gets fuel.</h2>
        <div className="hiw-setup">
          <h3>First, the pump owner approves credit</h3>
          <p>The pump owner sets the credit limit and terms, and the fleet owner accepts them. Each indent is checked against these agreed terms and the credit still available.</p>
        </div>
      </div>

      <ol className="hiw-strip">
        {SCENES.map((s, i) => (
          <li key={s.title} className="hiw-scene">
            <span className="hiw-num">{i + 1}</span>
            {s.picture}
            <h3>{s.title}</h3>
            <p>{s.caption}</p>
          </li>
        ))}
      </ol>

      <p className="hiw-note">The fleet owner and pump can see the saved fuel record even while confirmation is pending. Pump staff use the app on any phone; no new machine is needed. Example numbers only.</p>
    </section>
  );
}
